/**
 * Resume Scorecard Modal
 *
 * Deterministic, client-side readout of how the resume reads — headlined by the
 * "Authenticity" score (how human vs. AI-generated the writing sounds). No API
 * call, no cost, runs on the in-memory resume (including unsaved edits).
 *
 * Available to all users (trial/normal/active) — unlike the AI Review feature,
 * this is read-only guidance, not an AI-assisted rewrite.
 */
import { useRef, useMemo, useState } from 'react';
import { X, Gauge, ChevronDown, ChevronRight } from 'lucide-react';
import { useEscapeClose } from '../hooks/useEscapeClose';
import { useFocusTrap } from '../hooks/useFocusTrap';
import type { ResumeJSON } from '../types';
import { buildScorecard, scoreColor, type DimensionScore, type Severity } from '../utils/scorecard';

interface ScorecardModalProps {
  isOpen: boolean;
  onClose: () => void;
  resume: ResumeJSON;
}

const SEV_DOT: Record<Severity, string> = { high: 'bg-red-500', med: 'bg-amber-500', low: 'bg-gray-400' };

function ScoreRing({ score }: { score: number }) {
  const c = scoreColor(score);
  const r = 34, circ = 2 * Math.PI * r;
  const dash = (Math.max(0, Math.min(100, score)) / 100) * circ;
  const stroke = score >= 85 ? '#22c55e' : score >= 70 ? '#f59e0b' : '#ef4444';
  return (
    <div className="relative w-24 h-24 flex-shrink-0">
      <svg className="w-24 h-24 -rotate-90" viewBox="0 0 80 80">
        <circle cx="40" cy="40" r={r} fill="none" stroke="#e5e7eb" strokeWidth="7" />
        <circle cx="40" cy="40" r={r} fill="none" stroke={stroke} strokeWidth="7" strokeLinecap="round" strokeDasharray={`${dash} ${circ}`} />
      </svg>
      <div className={`absolute inset-0 flex flex-col items-center justify-center ${c.text}`}>
        <span className="text-2xl font-bold leading-none">{score}</span>
        <span className="text-[10px] text-gray-400">/ 100</span>
      </div>
    </div>
  );
}

function DimensionCard({ dim }: { dim: DimensionScore }) {
  const [open, setOpen] = useState(dim.key === 'authenticity');
  const c = scoreColor(dim.score);
  const hasFindings = dim.findings.length > 0;
  return (
    <div className={`border rounded-lg ${c.bg} border-gray-200`}>
      <div className="p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-sm font-semibold text-gray-800">{dim.label}</span>
            {dim.key === 'authenticity' && <span className="text-[10px] uppercase tracking-wide text-gray-400">headline</span>}
          </div>
          <span className={`text-sm font-bold ${c.text} flex-shrink-0`}>{dim.score === null ? 'N/A' : `${dim.score}`}</span>
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200 overflow-hidden">
          <div className={`h-full ${c.bar}`} style={{ width: `${dim.score ?? 0}%` }} />
        </div>
        <p className="text-xs text-gray-600 mt-2">{dim.blurb}</p>
      </div>
      {hasFindings && (
        <div className="border-t border-gray-200/70">
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="w-full flex items-center gap-1 px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700"
          >
            {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            {dim.findings.length} {dim.findings.length === 1 ? 'item' : 'items'} to look at
          </button>
          {open && (
            <ul className="px-3 pb-3 space-y-1.5 max-h-52 overflow-y-auto">
              {dim.findings.slice(0, 40).map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-xs">
                  <span className={`w-2 h-2 rounded-full mt-1 flex-shrink-0 ${SEV_DOT[f.severity]}`} />
                  <span className="min-w-0">
                    <span className="text-gray-700">{f.label}</span>
                    <span className="text-gray-400"> · {f.section}</span>
                    {f.detail && <span className="block text-gray-400 italic truncate">“{f.detail}”</span>}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export function ScorecardModal({ isOpen, onClose, resume }: ScorecardModalProps) {
  const modalRef = useRef<HTMLDivElement>(null);
  useEscapeClose(onClose, isOpen);
  useFocusTrap(modalRef, isOpen);
  const card = useMemo(() => buildScorecard(resume), [resume]);

  if (!isOpen) return null;
  const oc = scoreColor(card.overall);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog" aria-modal="true" aria-label="Resume Scorecard">
      <div ref={modalRef} className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <Gauge className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold">Resume Scorecard</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          <div className={`flex items-center gap-4 p-4 rounded-lg ${oc.bg} mb-4`}>
            <ScoreRing score={card.overall} />
            <div>
              <div className="text-sm font-semibold text-gray-800">Overall readiness</div>
              <p className="text-xs text-gray-600 mt-1">
                A deterministic read of how your resume comes across. The <span className="font-medium">Authenticity</span> score
                estimates how human (vs. AI-generated) the writing sounds — higher is better.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            {card.dimensions.map((d) => <DimensionCard key={d.key} dim={d} />)}
          </div>

          <p className="text-[11px] text-gray-400 mt-4 leading-relaxed">
            This is a guide, not a gate — it flags patterns, it doesn’t rewrite. Numbers and ambiguous words are scored
            conservatively, so use your judgment. For AI-assisted fixes, run <span className="italic">Review Resume and Generate Recommendations</span>.
          </p>
        </div>
      </div>
    </div>
  );
}
